import type {
  ObjectToolbarConfig,
  RegisterToolbarConfigArgs,
} from '@/blocks/object-toolbar';

/**
 * Assemble the object-toolbar config for the Internal Audit register/list page
 * (CUS-165). Register toolbars are type-level: no `parentObject` (permissions
 * resolve against the type) and no `save` (a list page has no form). The
 * register's multi-format export (CSV / PDF / custom PDF) is surfaced as the
 * toolbar's "Download" group — one action per `ExportOption` returned by
 * `useExportOptions`, the same source that drives the legacy `ExportButton`.
 * The create button is surfaced as the toolbar's primary action, gated on
 * `insert:internal_audit_entity`.
 *
 * Takes the shared {@link RegisterToolbarConfigArgs} (see
 * `blocks/object-toolbar/types.ts`) — the input shape every module's register
 * builder uses across the CUS-136 fanout. Pure so it can be unit-tested without
 * rendering the page.
 */
export const buildInternalAuditRegisterToolbarConfig = ({
  title,
  counter,
  exportOptions,
  create,
  editRibbon,
}: RegisterToolbarConfigArgs): ObjectToolbarConfig => ({
  header: { title, counter },
  actions: {
    ...(create
      ? {
          primary: [
            {
              id: 'create',
              label: create.label,
              iconName: 'plus',
              onClick: create.onClick,
              permission: 'insert:internal_audit_entity',
            },
          ],
        }
      : {}),
    ...(editRibbon
      ? {
          overflow: [
            {
              id: 'edit',
              label: editRibbon.groupLabel,
              actions: [
                {
                  id: 'edit-ribbon',
                  label: editRibbon.label,
                  iconName: 'filter-lines',
                  onClick: editRibbon.onClick,
                },
              ],
            },
          ],
        }
      : {}),
    export: [
      {
        id: 'export',
        actions: exportOptions.map((option) => ({
          id: option.id,
          label: option.text,
          iconName: 'download-01',
          onClick: option.exportFunction,
          disabled: option.disabled,
        })),
      },
    ],
  },
});
